import { Tag } from '@carbon/react';
import { DataTableColumnDef } from 'components/data-table';
import type { FormationSlot } from 'types/api-responses';
import { slotTableColumns } from 'pages/type-curves/slot-table-columns';
import style from './unit-builder-modal.module.scss';

/** X-extent of the scoped unit along its section line, as the gun barrel uses. */
export interface UnitXExtent {
  leftXFt: number;
  rightXFt: number;
}

/**
 * A row is a generated LTD slot rather than an existing well.
 *
 * Existing wells carry a UWI; generated slots do not. `wellStatus` is checked
 * too because the gun barrel legend labels generated inventory "LTD".
 *
 * @param {FormationSlot} row - The slot row.
 * @return {boolean} True for a generated slot.
 */
export const isGeneratedSlot = (row: FormationSlot): boolean =>
  !row.uwi || (row.wellStatus ?? '').toUpperCase() === 'LTD';

/**
 * Whether the row counts as assigned to the scoped unit.
 *
 * The mock's rule, verbatim from its `SlotRow` docs: LTD slots inside the scoped
 * unit are the unit's own inventory (true); existing wells inside it are not —
 * the LTDs carry the development. Rows outside the scoped unit sit in their own
 * unit, so they are assigned there (true).
 *
 * @param {FormationSlot} row - The slot row.
 * @param {string | null} scopedUnitName - The unit the builder is scoped to.
 * @return {boolean} Whether the row is assigned to its unit.
 */
export const isAssignedToUnit = (
  row: FormationSlot,
  scopedUnitName: string | null
): boolean => {
  const inScopedUnit =
    scopedUnitName !== null && row.developmentAreaName === scopedUnitName;
  if (!inScopedUnit) return true;
  return isGeneratedSlot(row);
};

/**
 * Whether the row's geometry reaches into the scoped unit.
 *
 * Independent of assignment — a neighbour's lateral can reach into a unit it is
 * not assigned to, which is exactly what this column is for.
 *
 * NOTE: approximated from `xOffsetFt` against the unit's section-line extent,
 * because the slots query returns no lateral geometry. That is an overlap along
 * one axis, not a true polygon intersection, so a lateral running parallel and
 * outside the unit can read as intersecting. Replace with a server-computed flag
 * when one exists.
 *
 * @param {FormationSlot} row - The slot row.
 * @param {UnitXExtent | null} extent - The scoped unit's section-line extent.
 * @return {boolean | null} True/false, or null when it cannot be determined.
 */
export const intersectsUnit = (
  row: FormationSlot,
  extent: UnitXExtent | null
): boolean | null => {
  if (!extent || row.xOffsetFt === null || row.xOffsetFt === undefined) {
    return null;
  }
  return row.xOffsetFt >= extent.leftXFt && row.xOffsetFt <= extent.rightXFt;
};

const yesNo = (value: boolean | null): string => {
  if (value === null) return '--';
  return value ? 'Yes' : 'No';
};

/**
 * Pull a shared column def by id, so this table can order columns differently
 * without duplicating their renderers.
 *
 * @param {string} id - Column id in `slotTableColumns`.
 * @return {DataTableColumnDef<FormationSlot>} The shared column def.
 */
const shared = (id: string): DataTableColumnDef<FormationSlot> => {
  const column = slotTableColumns.find((c) => c.id === id);
  if (!column) throw new Error(`slotTableColumns has no column "${id}"`);
  return column;
};

/**
 * The builder's slot columns, in the mock's order: Name, UWI, Unit, Assigned to
 * Unit, Intersects the Unit, Formation, Slot ID, then everything the shared
 * table already carries.
 *
 * @param {string | null} scopedUnitName - The unit the builder is scoped to.
 * @param {UnitXExtent | null} extent - That unit's section-line extent.
 * @return {DataTableColumnDef<FormationSlot>[]} The column set.
 */
export const buildUnitBuilderSlotColumns = (
  scopedUnitName: string | null,
  extent: UnitXExtent | null
): DataTableColumnDef<FormationSlot>[] => {
  const leading: DataTableColumnDef<FormationSlot>[] = [
    {
      id: 'name',
      header: 'Name',
      size: 180,
      // A well reads as its name ("LEASE #NUMBER"), falling back to its UWI
      // when it has none; generated slots have neither, so they read as their
      // slot id, matching the mock's "Slot N".
      // A composite leg reads as its composite (the mock), via `compositeName`.
      accessorFn: (row) =>
        row.compositeName || row.wellName || row.uwi || row.slotId || '',
      // An unvalued LTD slot is struck through with an "Unvalued" pill, as the
      // mock shows — it stays selectable (it is still an LTD).
      cell: (info) => {
        const row = info.row.original;
        const name = (info.getValue() as string) || '--';
        const unvalued = isGeneratedSlot(row) && row.valued === false;
        if (!unvalued) return name;
        return (
          <span className={style.unvaluedName}>
            <span className={style.strikethrough}>{name}</span>
            <Tag type="red" size="sm">
              Unvalued
            </Tag>
          </span>
        );
      },
      meta: { type: 'text' },
    },
    shared('uwi'),
    shared('developmentAreaName'),
    {
      id: 'assignedToUnit',
      header: 'Assigned to Unit',
      size: 150,
      accessorFn: (row) => yesNo(isAssignedToUnit(row, scopedUnitName)),
      meta: { type: 'enum' },
    },
    {
      id: 'intersectsUnit',
      header: 'Intersects the Unit',
      size: 160,
      accessorFn: (row) => yesNo(intersectsUnit(row, extent)),
      meta: { type: 'enum' },
    },
    shared('formation'),
    shared('slotId'),
  ];

  const leadingIds = new Set(leading.map((c) => c.id));
  return [...leading, ...slotTableColumns.filter((c) => !leadingIds.has(c.id))];
};
